<template>
  <Modal
    :active="active"
    :close-disabled="isDeleting"
    :aria-busy="isDeleting"
    @close="emits('close')"
  >
    <template #header>
      {{ $t("documentSpaces.deleteConfirmation.title") }}
    </template>

    <template #body>
      {{ $t("documentSpaces.deleteConfirmation.message", { name: name }) }}
      <div v-if="deleteError" class="message error" role="alert">
        {{ $t("error") }}
      </div>
    </template>

    <template #footer>
      <div class="buttons">
        <button
          class="button secondary"
          :disabled="isDeleting"
          @click="emits('close')"
        >
          {{ $t("cancel") }}
        </button>
        <button
          class="button dangerous"
          :disabled="isDeleting"
          @click="deleteSharedSpace"
        >
          {{ $t("documentSpaces.deleteConfirmation.submit") }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, onBeforeUnmount, watch } from "vue";
import { Modal } from "@/components";
import { useDeleteDocumentSpaceMutation } from "@/graphql/operations";
import { useFlashMessage } from "@/hooks";
import { useI18n } from "vue-i18n";

const props = defineProps({
  active: {
    type: Boolean,
    required: true,
  },
  spaceId: {
    type: String,
    required: true,
  },
  name: {
    type: String,
  },
});

const emits = defineEmits(["close", "success"]);

const { displayFlashMessage } = useFlashMessage();
const { t } = useI18n();

const { executeMutation: executeDelete } = useDeleteDocumentSpaceMutation();
const isDeleting = ref(false);
const deleteError = ref(false);
watch(
  () => props.active,
  () => {
    deleteError.value = false;
  },
);
let mounted = true;
onBeforeUnmount(() => {
  mounted = false;
});
const deleteSharedSpace = async () => {
  if (isDeleting.value) return;
  isDeleting.value = true;
  deleteError.value = false;
  try {
    const result = await executeDelete({ id: props.spaceId });
    if (!mounted) return;
    if (result.error || !result.data?.deleteDocumentSpace) {
      deleteError.value = true;
      return;
    }
    displayFlashMessage(t("documentSpaces.deleteConfirmation.success"));
    emits("success");
  } catch {
    if (mounted) deleteError.value = true;
  } finally {
    isDeleting.value = false;
  }
};
</script>

<style scoped>
.message {
  box-sizing: border-box;
  margin-top: 1rem;
}
</style>
